import { Controller } from '@hotwired/stimulus';
import { calcAutoPan, infiniteExtent, PanOnScrollMode, snapPosition, XYPanZoom } from '@xyflow/system';
import { paint, typing } from '../lib/dom';
import { anchor, edgePath, heading, mcpPath, pathBetween, WIRE_SNAP_Y } from '../lib/workflow/geometry';
import { summarizeConditions } from '../lib/conditions';

const GRID = 16;
const CARD_WIDTH = 256;
const CANVAS_MARGIN = 16;
const STEP_TOP = 160;
const DRAG_THRESHOLD = 4;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 2;
const ZOOM_STEP = 1.25;
const FIT_DURATION = 200;
const FIT_PADDING = 60;
const SNAP_GRID = [GRID, GRID];
const COLUMN_GAP = 64;
const ROW_GAP = 144;
const TRIGGER_RANK = -1;
const EDGE_WIDTH = '1';
const MCP_OFFSET_X = 300;
const MCP_GAP_Y = 76;
const MCP_ALIGN_Y = 11;
const MCP_PALETTE = [
  'bg-accent-soft text-accent',
  'bg-agent-soft text-agent',
  'bg-script-soft text-script',
  'bg-ok-soft text-ok',
  'bg-warn-soft text-warn',
  'bg-danger-soft text-danger',
];
const HIT_WIDTH = '14';
const GRIP_RADIUS = '5';
const CUT_RADIUS = '9';
const CUT_ARM = 3;
const ARROW_SIZE = 5;
const ARROW_SPREAD = 0.6;
const SVG_NS = 'http://www.w3.org/2000/svg';
const HOVER_ACTIONS = 'pointerenter->workflow-canvas#hoverWire pointerleave->workflow-canvas#leaveWire';

export default class extends Controller {
  static targets = [
    'canvas', 'canvasAdd', 'canvasEmpty', 'cardTemplate', 'mcpCardTemplate', 'panTool', 'triggerCardTemplate',
  ];

  static classes = ['cardActive', 'cardIdle'];

  static values = { labels: Object };

  initialize() {
    this.scene = { workflow: { steps: [], triggers: [] }, cards: new Map(), selection: new Set() };
    this.viewport = { x: 0, y: 0, zoom: 1 };
    this.stepCards = [];
    this.triggerCards = [];
    this.panHeld = false;
    this.panTool = false;
    this.hovered = null;
  }

  connect() {
    this.holdPan = (event) => this.#holdPan(event);
    this.releasePan = (event) => this.#releasePan(event);
    this.marqueeMove = (event) => this.#marqueeMove(event);
    this.marqueeEnd = () => this.#marqueeEnd();

    window.addEventListener('keydown', this.holdPan);
    window.addEventListener('keyup', this.releasePan);

    this.panZoom = XYPanZoom({
      domNode: this.canvasTarget,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      viewport: this.viewport,
      translateExtent: infiniteExtent,
      onDraggingChange: (dragging) => this.canvasTarget.classList.toggle('cursor-grabbing', dragging),
    });

    this.#renderPanTool();
    this.#fitOnce();
  }

  disconnect() {
    window.removeEventListener('keydown', this.holdPan);
    window.removeEventListener('keyup', this.releasePan);
    this.panZoom.destroy();
    cancelAnimationFrame(this.wireFrame);
    this.#stopDragging();
    this.#stopConnecting();
    this.#stopMarquee();
  }

  render(scene) {
    this.scene = scene;
    this.layout = this.#tidySpots();
    this.triggerCards = scene.workflow.triggers.map((trigger) => this.#triggerCard(trigger));
    this.stepCards = scene.workflow.steps.map((step, index) => this.#stepCard(step, index));
    this.mcpCards = scene.workflow.steps.flatMap((step) => this.#mcpCardsOf(step));
    this.wireLayer = this.#svgLayer('z-0');
    this.toolLayer = this.#svgLayer('z-10');
    this.hovered = null;

    this.#stage().replaceChildren(
      this.wireLayer,
      ...this.triggerCards,
      ...this.stepCards,
      ...this.mcpCards,
      this.toolLayer
    );
    this.#settleMcpCards();
    this.#paintViewport();
    this.#drawWires();

    const populated = scene.workflow.steps.length > 0 || scene.workflow.triggers.length > 0;

    this.canvasAddTarget.hidden = !populated;
    this.canvasEmptyTarget.hidden = populated;

    this.#fitOnce();
  }

  refreshWires() {
    this.#refreshPorts();
    this.#drawWires();
  }

  paintSelection() {
    const opened = this.scene.openId;

    this.#stage().querySelectorAll('[data-node-id]').forEach((card) => {
      this.#paintCard(card, card.dataset.nodeId, card.dataset.nodeId === opened);
    });
  }

  focus(id) {
    const box = this.#nodeBoxes().find((node) => node.id === id);

    if (!box) return;

    const rect = this.canvasTarget.getBoundingClientRect();
    const zoom = this.viewport.zoom;

    this.panZoom.setViewport(
      {
        x: rect.width / 2 - (box.x + box.width / 2) * zoom,
        y: rect.height / 2 - (box.y + box.height / 2) * zoom,
        zoom,
      },
      { duration: FIT_DURATION }
    );
  }

  zoomIn() {
    this.panZoom.scaleBy(ZOOM_STEP, { duration: FIT_DURATION });
  }

  zoomOut() {
    this.panZoom.scaleBy(1 / ZOOM_STEP, { duration: FIT_DURATION });
  }

  fitView() {
    this.#fit(MAX_ZOOM, FIT_DURATION);
  }

  tidy() {
    if (!this.scene.draggable) return;

    const spots = this.#tidySpots();

    if (spots.size === 0) return;

    [...this.triggerCards, ...this.stepCards].forEach((card) => {
      const spot = spots.get(card.dataset.nodeId);
      const node = this.#node(card.dataset.nodeId);

      node.canvasX = spot.x;
      node.canvasY = spot.y;
      card.style.left = `${spot.x}px`;
      card.style.top = `${spot.y}px`;
    });

    this.#placeMcpCards();
    this.#drawWires();
    this.dispatch('moved');
    this.#fit(MAX_ZOOM, FIT_DURATION);
  }

  togglePanTool() {
    this.panTool = !this.panTool;

    this.#renderPanTool();
  }

  startCanvasPointer(event) {
    if (event.button !== 0) return;
    if (event.target !== this.canvasTarget && event.target !== this.stageEl) return;
    if (this.#panning()) return;

    this.#startMarquee(event);
  }

  startDrag(event) {
    if (!this.scene.draggable) return;
    if (this.#panning()) return;
    if (event.target.closest('[data-role="conditions"]')) return;

    if (this.#additive(event)) return;

    const card = event.currentTarget;
    const id = card.dataset.nodeId;

    if (!this.scene.selection.has(id)) this.#announce(new Set([id]), false);

    const cards = this.#selectedCards();

    this.drag = {
      id,
      card,
      cards,
      pointerX: event.clientX,
      pointerY: event.clientY,
      pointer: { x: event.clientX, y: event.clientY },
      viewportX: this.viewport.x,
      viewportY: this.viewport.y,
      frame: null,
      moved: false,
    };

    this.dragMove = (moving) => this.#dragMove(moving);
    this.dragEnd = () => this.#dragEnd();

    window.addEventListener('pointermove', this.dragMove);
    window.addEventListener('pointerup', this.dragEnd);
  }

  startConnect(event) {
    if (!this.scene.draggable) return;

    this.#beginConnection(event, {
      anchorId: event.currentTarget.closest('[data-node-id]').dataset.nodeId,
      end: event.currentTarget.dataset.end,
      parentId: null,
      childId: null,
    });
  }

  startReconnect(event) {
    if (!this.scene.draggable) return;

    event.stopPropagation();

    const { parent, child, end } = event.currentTarget.dataset;

    this.#beginConnection(event, {
      anchorId: end === 'target' ? parent : child,
      end,
      parentId: parent,
      childId: child,
    });
  }

  hoverWire({ currentTarget }) {
    const { parent, child } = currentTarget.dataset;

    if (!child || this.connection || this.drag) return;

    this.hovered = { parent, child };

    currentTarget.classList.add('is-live');
    this.#drawTools();
  }

  leaveWire({ relatedTarget }) {
    if (!this.hovered || this.#stillOnWire(relatedTarget)) return;

    this.#hoveredWire()?.classList.remove('is-live');
    this.hovered = null;
    this.#drawTools();
  }

  removeDependency({ currentTarget }) {
    const { child, parent } = currentTarget.dataset;

    this.dispatch('unlink', { detail: { childId: child, parentId: parent } });
  }

  #stage() {
    if (!this.stageEl || this.stageEl.parentElement !== this.canvasTarget) {
      this.stageEl = document.createElement('div');
      this.stageEl.className = 'absolute top-0 left-0 origin-top-left';
      this.stageEl.setAttribute('data-turbo-temporary', '');
      this.canvasTarget.prepend(this.stageEl);
    }

    return this.stageEl;
  }

  #tunePanZoom() {
    this.panZoom.update({
      noWheelClassName: 'nowheel',
      noPanClassName: 'nopan',
      preventScrolling: true,
      panOnScroll: false,
      panOnScrollMode: PanOnScrollMode.Free,
      panOnScrollSpeed: 0.5,
      panOnDrag: this.#panButtons(),
      userSelectionActive: false,
      zoomOnPinch: true,
      zoomOnScroll: true,
      zoomOnDoubleClick: false,
      zoomActivationKeyPressed: false,
      connectionInProgress: false,
      paneClickDistance: DRAG_THRESHOLD,
      lib: 'workflow',
      onTransformChange: ([x, y, zoom]) => this.#trackViewport({ x, y, zoom }),
    });
  }

  #panButtons() {
    return this.#panning() ? [0, 1] : [1];
  }

  #trackViewport(viewport) {
    this.viewport = viewport;

    this.#paintViewport();
  }

  #paintViewport() {
    const { x, y, zoom } = this.viewport;

    if (this.stageEl) this.stageEl.style.transform = `translate(${x}px, ${y}px) scale(${zoom})`;

    this.canvasTarget.style.backgroundPosition = `${x}px ${y}px`;
    this.canvasTarget.style.backgroundSize = `${GRID * zoom}px ${GRID * zoom}px`;
  }

  #tidySpots() {
    const steps = this.scene.workflow.steps;
    const triggers = this.scene.workflow.triggers;

    if (steps.length === 0 && triggers.length === 0) return new Map();

    const ranks = this.#ranks(steps);
    const rows = new Map();

    steps.forEach((step) => {
      const rank = ranks.get(step.id);

      rows.set(rank, [...(rows.get(rank) ?? []), step.id]);
    });

    if (triggers.length > 0) rows.set(TRIGGER_RANK, triggers.map((trigger) => trigger.id));

    const widest = Math.max(...[...rows.values()].map((ids) => ids.length));
    const axis = CANVAS_MARGIN + (widest * (CARD_WIDTH + COLUMN_GAP) - COLUMN_GAP) / 2;
    const spots = new Map();

    for (const [rank, ids] of rows) {
      const span = ids.length * (CARD_WIDTH + COLUMN_GAP) - COLUMN_GAP;
      const startX = axis - span / 2;

      ids.forEach((id, index) => {
        spots.set(
          id,
          snapPosition({ x: startX + index * (CARD_WIDTH + COLUMN_GAP), y: STEP_TOP + rank * ROW_GAP }, SNAP_GRID)
        );
      });
    }

    return spots;
  }

  #ranks(steps) {
    const known = new Set(steps.map((step) => step.id));
    const ranks = new Map(steps.map((step) => [step.id, 0]));

    for (let pass = 0; pass < steps.length; pass++) {
      let settled = true;

      for (const step of steps) {
        const parents = step.dependsOn.filter((id) => known.has(id));
        const rank = parents.length === 0 ? 0 : Math.max(...parents.map((id) => ranks.get(id))) + 1;

        if (rank === ranks.get(step.id)) continue;

        ranks.set(step.id, rank);
        settled = false;
      }

      if (settled) break;
    }

    return ranks;
  }

  #fitOnce() {
    if (this.fitted || !this.panZoom || !this.#nodeBoxes().length) return;

    this.fitted = true;

    this.#fit(1, 0);
  }

  #fit(maxZoom, duration) {
    const boxes = this.#nodeBoxes();

    if (!boxes.length) {
      this.panZoom.setViewport({ x: 0, y: 0, zoom: 1 }, { duration });
      return;
    }

    const rect = this.canvasTarget.getBoundingClientRect();

    if (!rect.width || !rect.height) return;

    const minX = Math.min(...boxes.map((box) => box.x));
    const minY = Math.min(...boxes.map((box) => box.y));
    const width = Math.max(...boxes.map((box) => box.x + box.width)) - minX;
    const height = Math.max(...boxes.map((box) => box.y + box.height)) - minY;
    const scale = Math.min((rect.width - FIT_PADDING * 2) / width, (rect.height - FIT_PADDING * 2) / height);
    const zoom = Math.min(maxZoom, Math.max(MIN_ZOOM, scale));

    this.panZoom.setViewport(
      {
        x: rect.width / 2 - (minX + width / 2) * zoom,
        y: rect.height / 2 - (minY + height / 2) * zoom,
        zoom,
      },
      { duration }
    );
  }

  #renderPanTool() {
    const tools = this.labelsValue.tools;

    this.panToolTarget.setAttribute('aria-label', this.panTool ? tools.select : tools.pan);

    for (const icon of this.panToolTarget.querySelectorAll('[data-tool-icon]')) {
      this.#reveal(icon, (icon.dataset.toolIcon === 'select') === this.panTool);
    }

    this.#paintPanCursor();
    this.#tunePanZoom();
  }

  #paintPanCursor() {
    this.canvasTarget.classList.toggle('cursor-grab', this.#panning());
  }

  #panning() {
    return this.panTool || this.panHeld;
  }

  #holdPan(event) {
    if (event.code !== 'Space' || this.panHeld || typing(event.target)) return;

    this.panHeld = true;

    this.#paintPanCursor();
    this.#tunePanZoom();
  }

  #releasePan(event) {
    if (event.code !== 'Space') return;

    this.panHeld = false;

    this.#paintPanCursor();
    this.#tunePanZoom();
  }

  #startMarquee(event) {
    const rect = this.canvasTarget.getBoundingClientRect();
    const originX = event.clientX - rect.left;
    const originY = event.clientY - rect.top;
    const base = event.shiftKey ? new Set(this.scene.selection) : new Set();

    this.marquee = { base, originX, originY, x: originX, y: originY, moved: false };

    window.addEventListener('pointermove', this.marqueeMove);
    window.addEventListener('pointerup', this.marqueeEnd);
  }

  #marqueeMove(event) {
    if (!this.marquee) return;

    const rect = this.canvasTarget.getBoundingClientRect();

    this.marquee.x = event.clientX - rect.left;
    this.marquee.y = event.clientY - rect.top;

    const dx = this.marquee.x - this.marquee.originX;
    const dy = this.marquee.y - this.marquee.originY;

    if (!this.marquee.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    this.marquee.moved = true;

    this.#drawMarquee();
    this.#announce(new Set([...this.marquee.base, ...this.#marqueeIds()]), false);
  }

  #marqueeEnd() {
    if (!this.marquee) return;

    const emptied = !this.marquee.moved;

    this.#stopMarquee();

    this.#announce(emptied ? new Set() : this.scene.selection, true);

    this.marquee = null;
  }

  #stopMarquee() {
    window.removeEventListener('pointermove', this.marqueeMove);
    window.removeEventListener('pointerup', this.marqueeEnd);
    this.marqueeEl?.remove();
    this.marqueeEl = null;
  }

  #drawMarquee() {
    if (!this.marqueeEl) {
      this.marqueeEl = document.createElement('div');
      this.marqueeEl.className = 'pointer-events-none absolute rounded-sm border border-accent bg-accent/10';
      this.canvasTarget.appendChild(this.marqueeEl);
    }

    const box = this.#marqueeScreenBox();

    this.marqueeEl.style.left = `${box.x}px`;
    this.marqueeEl.style.top = `${box.y}px`;
    this.marqueeEl.style.width = `${box.width}px`;
    this.marqueeEl.style.height = `${box.height}px`;
  }

  #marqueeScreenBox() {
    return {
      x: Math.min(this.marquee.originX, this.marquee.x),
      y: Math.min(this.marquee.originY, this.marquee.y),
      width: Math.abs(this.marquee.x - this.marquee.originX),
      height: Math.abs(this.marquee.y - this.marquee.originY),
    };
  }

  #marqueeIds() {
    const screen = this.#marqueeScreenBox();
    const { x, y, zoom } = this.viewport;
    const box = {
      minX: (screen.x - x) / zoom,
      minY: (screen.y - y) / zoom,
      maxX: (screen.x + screen.width - x) / zoom,
      maxY: (screen.y + screen.height - y) / zoom,
    };
    const hits = this.#nodeBoxes().filter((node) => this.#within(node, box));

    return hits.map((node) => node.id);
  }

  #within(node, box) {
    return node.x < box.maxX && node.x + node.width > box.minX && node.y < box.maxY && node.y + node.height > box.minY;
  }

  #additive(event) {
    return event.metaKey || event.ctrlKey;
  }

  #announce(selection, settled) {
    this.scene.selection = selection;

    this.paintSelection();
    this.dispatch('selectionchange', { detail: { selection, settled } });
  }

  #paintCard(node, id, open) {
    const surface = node.querySelector('[data-role="badge"]') ?? node;

    paint(surface, open || this.scene.selection.has(id), this.cardActiveClasses, this.cardIdleClasses);
  }

  #svgLayer(depth) {
    const svg = document.createElementNS(SVG_NS, 'svg');

    svg.setAttribute('class', `pointer-events-none absolute inset-0 overflow-visible ${depth}`);

    return svg;
  }

  #scheduleWires() {
    if (this.wireFrame) return;

    this.wireFrame = requestAnimationFrame(() => {
      this.wireFrame = null;

      this.#drawWires();
    });
  }

  #drawWires() {
    if (!this.wireLayer) return;

    const cardsById = new Map(this.stepCards.map((card) => [card.dataset.stepId, card]));
    const wires = this.scene.workflow.steps.flatMap((child) => {
      const target = cardsById.get(child.id);

      return child.dependsOn.flatMap((parentId) => {
        const source = cardsById.get(parentId);

        if (!source || !target || this.#detached(parentId, child.id)) return [];

        return this.#wire(source, target, parentId, child.id, 'step');
      });
    });
    this.scene.workflow.triggers.forEach((trigger) => {
      const source = this.#card(trigger.id);

      trigger.starts.forEach((stepId) => {
        const target = cardsById.get(stepId);

        if (!source || !target || this.#detached(trigger.id, stepId)) return;

        wires.unshift(this.#wire(source, target, trigger.id, stepId, 'trigger'));
      });
    });

    this.scene.workflow.steps.forEach((step) => {
      const source = cardsById.get(step.id);

      (step.mcpServers ?? []).forEach((attachment) => {
        if (!attachment.mcpServerId) return;

        const target = this.#card(this.#mcpId(step, attachment));

        if (!source || !target) return;

        wires.push(this.#wire(source, target, step.id, this.#mcpId(step, attachment), 'mcp'));
      });
    });

    this.wireLayer.replaceChildren(...wires, ...(this.tempWire ? [this.tempWire] : []));
    this.#equipWires();
    this.#drawTools();
  }

  #drawTools() {
    if (!this.toolLayer) return;

    const wire = this.#hoveredWire();

    this.toolLayer.replaceChildren(...(wire ? [this.#wireTools(wire.firstElementChild, wire.dataset)] : []));
  }

  #stillOnWire(node) {
    if (!(node instanceof Element)) return false;

    return node.closest('.wire-tools') !== null || this.#hoveredWire()?.contains(node) === true;
  }

  #hoveredWire() {
    if (!this.hovered) return null;

    const { parent, child } = this.hovered;

    return this.wireLayer.querySelector(`[data-parent="${parent}"][data-child="${child}"]`);
  }

  #detached(parentId, childId) {
    const link = this.connection;

    return Boolean(link?.moved && link.parentId === parentId && link.childId === childId);
  }

  #wire(source, target, parentId, childId, kind) {
    const d = kind === 'mcp' ? mcpPath(source, target) : edgePath(source, target);
    const group = document.createElementNS(SVG_NS, 'g');
    const strokes = { trigger: 'var(--ok)', mcp: 'var(--agent)' };
    const line = this.#path(d, strokes[kind] ?? 'var(--edge)', EDGE_WIDTH);
    const hit = this.#path(d, 'transparent', HIT_WIDTH);

    group.setAttribute('class', this.#live(parentId, childId) ? 'wire is-live' : 'wire');
    group.classList.add(...this.#focusClasses(parentId, childId));
    group.dataset.parent = parentId;
    group.dataset.child = childId;
    group.dataset.kind = kind;

    if (kind === 'mcp') line.setAttribute('stroke-dasharray', '4 4');
    if (this.scene.draggable && kind !== 'mcp') group.setAttribute('data-action', HOVER_ACTIONS);

    line.setAttribute('class', 'wire-line');
    line.setAttribute('pointer-events', 'none');
    line.setAttribute('stroke-opacity', '1');

    hit.setAttribute('pointer-events', 'stroke');
    group.append(line, hit);

    return group;
  }

  #live(parentId, childId) {
    return this.hovered?.parent === parentId && this.hovered?.child === childId;
  }

  #focusClasses(parentId, childId) {
    const focused = this.scene.openId;

    if (!focused || !this.scene.selection.has(focused)) return [];
    if (!this.scene.workflow.steps.some((step) => step.id === focused)) return [];

    return childId === focused ? ['is-dep-in'] : ['is-dimmed'];
  }

  #equipWires() {
    for (const group of this.wireLayer.querySelectorAll('.wire:not([data-kind="mcp"])')) {
      group.append(this.#arrow(group.firstElementChild));
    }
  }

  #arrow(line) {
    const middle = line.getTotalLength() / 2;
    const tip = line.getPointAtLength(middle + ARROW_SIZE / 2);
    const slope = heading(line.getPointAtLength(middle - ARROW_SIZE / 2), tip);
    const wing = (spread) => {
      const angle = slope - spread;

      return `L ${tip.x - ARROW_SIZE * Math.cos(angle)} ${tip.y - ARROW_SIZE * Math.sin(angle)}`;
    };
    const arrow = this.#path(
      `M ${tip.x} ${tip.y} ${wing(ARROW_SPREAD)} M ${tip.x} ${tip.y} ${wing(-ARROW_SPREAD)}`,
      'var(--edge)',
      EDGE_WIDTH
    );

    arrow.setAttribute('class', 'wire-line wire-arrow');
    arrow.setAttribute('stroke-linecap', 'round');
    arrow.setAttribute('stroke-opacity', line.getAttribute('stroke-opacity'));
    arrow.setAttribute('pointer-events', 'none');

    return arrow;
  }

  #wireTools(line, { parent, child }) {
    const total = line.getTotalLength();
    const tools = document.createElementNS(SVG_NS, 'g');

    tools.setAttribute('class', 'wire-tools');
    tools.setAttribute('data-action', 'pointerleave->workflow-canvas#leaveWire');
    tools.append(
      this.#grip(line.getPointAtLength(0), parent, child, 'source'),
      this.#grip(line.getPointAtLength(total), parent, child, 'target'),
      this.#cut(line.getPointAtLength(total / 2), parent, child)
    );

    return tools;
  }

  #grip(point, parentId, childId, end) {
    const grip = document.createElementNS(SVG_NS, 'circle');

    grip.setAttribute('class', 'wire-grip');
    grip.setAttribute('cx', point.x);
    grip.setAttribute('cy', point.y);
    grip.setAttribute('r', GRIP_RADIUS);
    grip.setAttribute('fill', 'var(--canvas)');
    grip.setAttribute('stroke', 'var(--accent)');
    grip.setAttribute('stroke-width', '1.5');
    grip.setAttribute('data-action', 'pointerdown->workflow-canvas#startReconnect');
    grip.dataset.parent = parentId;
    grip.dataset.child = childId;
    grip.dataset.end = end;
    grip.append(this.#hint(this.labelsValue.wire.move));

    return grip;
  }

  #cut(point, parentId, childId) {
    const button = document.createElementNS(SVG_NS, 'g');
    const disc = document.createElementNS(SVG_NS, 'circle');
    const arms = [
      `M ${point.x - CUT_ARM} ${point.y - CUT_ARM} L ${point.x + CUT_ARM} ${point.y + CUT_ARM}`,
      `M ${point.x + CUT_ARM} ${point.y - CUT_ARM} L ${point.x - CUT_ARM} ${point.y + CUT_ARM}`,
    ].join(' ');
    const cross = this.#path(arms, 'var(--danger)', '1.5');

    disc.setAttribute('cx', point.x);
    disc.setAttribute('cy', point.y);
    disc.setAttribute('r', CUT_RADIUS);
    disc.setAttribute('fill', 'var(--canvas)');
    disc.setAttribute('stroke', 'var(--edge)');
    cross.setAttribute('stroke-linecap', 'round');
    cross.setAttribute('pointer-events', 'none');
    button.setAttribute('class', 'wire-cut');
    button.setAttribute('data-action', 'click->workflow-canvas#removeDependency');
    button.dataset.parent = parentId;
    button.dataset.child = childId;
    button.append(disc, cross, this.#hint(this.labelsValue.wire.remove));

    return button;
  }

  #hint(text) {
    const title = document.createElementNS(SVG_NS, 'title');

    title.textContent = text;

    return title;
  }

  #path(d, stroke, width) {
    const path = document.createElementNS(SVG_NS, 'path');

    path.setAttribute('d', d);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', stroke);
    path.setAttribute('stroke-width', width);

    return path;
  }

  #stepCard(step, index) {
    const node = this.cardTemplateTarget.content.firstElementChild.cloneNode(true);
    const card = this.scene.cards.get(step.id);
    const spot = this.#position(step);

    node.dataset.stepId = step.id;
    node.dataset.nodeId = step.id;
    node.style.left = `${spot.x}px`;
    node.style.top = `${spot.y}px`;
    this.#role(node, 'position').textContent = index + 1;
    this.#role(node, 'name').textContent = card.title;
    this.#role(node, 'detail').textContent = card.detail;
    this.#tile(node, card);
    this.#paintCard(node, step.id, step.id === this.scene.openId);
    this.#stepPorts(node, step);
    this.#conditionsMarker(node, step);

    if (!this.scene.draggable) node.classList.remove('cursor-grab');

    return node;
  }

  #triggerCard(trigger) {
    const node = this.triggerCardTemplateTarget.content.firstElementChild.cloneNode(true);
    const card = this.scene.cards.get(trigger.id);
    const spot = this.#triggerSpot(trigger);

    node.dataset.triggerId = trigger.id;
    node.dataset.nodeId = trigger.id;
    node.style.left = `${spot.x}px`;
    node.style.top = `${spot.y}px`;
    this.#role(node, 'title').textContent = card.title;
    this.#role(node, 'detail').textContent = card.detail;
    this.#role(node, 'title').classList.toggle('font-mono', card.mono);
    this.#tile(node, card);
    this.#paintCard(node, trigger.id, trigger.id === this.scene.openId);
    this.#port(node, 'outlet', trigger.starts.length > 0);

    if (!this.scene.draggable) node.classList.remove('cursor-grab');

    return node;
  }

  #conditionsMarker(node, step) {
    const dot = this.#role(node, 'conditions');
    const source = (step.conditions ?? '').trim();
    const blank = !source || source.replace(/\s/g, '') === '{}';

    dot.hidden = blank;

    if (blank) return;

    this.#role(node, 'conditions-summary').textContent = this.#conditionsSummary(source);
  }

  #conditionsSummary(source) {
    try {
      const parsed = JSON.parse(source);

      return summarizeConditions(parsed, this.labelsValue.conditions) ?? JSON.stringify(parsed, null, 2);
    } catch {
      return source;
    }
  }

  #refreshPorts() {
    this.stepCards.forEach((card) => this.#stepPorts(card, this.#node(card.dataset.stepId)));
    this.triggerCards.forEach((card) => {
      this.#port(card, 'outlet', this.#node(card.dataset.nodeId).starts.length > 0);
    });
  }

  #stepPorts(node, step) {
    this.#port(node, 'inlet', this.#wiredAbove(step));
    this.#port(node, 'outlet', this.#wiredBelow(step));
  }

  #port(node, role, wired) {
    const port = this.#role(node, role);

    port.classList.toggle('is-wired', wired);
    port.hidden = !this.scene.draggable && !wired;
  }

  #wiredAbove(step) {
    if (step.dependsOn.length > 0) return true;

    return this.scene.workflow.triggers.some((trigger) => trigger.starts.includes(step.id));
  }

  #wiredBelow(step) {
    return this.scene.workflow.steps.some((other) => other.dependsOn.includes(step.id));
  }

  #position(step) {
    return this.#spotFor(step, this.layout.get(step.id));
  }

  #triggerSpot(trigger) {
    return this.#spotFor(trigger, this.layout.get(trigger.id));
  }

  #spotFor(node, fallback) {
    if (Number.isInteger(node.canvasX) && Number.isInteger(node.canvasY)) {
      return { x: node.canvasX, y: node.canvasY };
    }

    return fallback;
  }

  #nodeBoxes() {
    return [...this.triggerCards, ...this.stepCards, ...(this.mcpCards ?? [])].map((card) => ({
      id: card.dataset.nodeId,
      x: parseFloat(card.style.left) || 0,
      y: parseFloat(card.style.top) || 0,
      width: card.offsetWidth,
      height: card.offsetHeight,
    }));
  }

  #selectedCards() {
    const cards = [...this.#stage().querySelectorAll('[data-node-id]')];
    const picked = cards.filter((card) => this.scene.selection.has(card.dataset.nodeId));

    return picked.map((card) => ({
      id: card.dataset.nodeId,
      card,
      originX: parseFloat(card.style.left) || 0,
      originY: parseFloat(card.style.top) || 0,
    }));
  }

  #dragMove(event) {
    const dx = event.clientX - this.drag.pointerX;
    const dy = event.clientY - this.drag.pointerY;

    if (!this.drag.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    this.drag.moved = true;
    this.drag.pointer = { x: event.clientX, y: event.clientY };

    this.#placeDragged();
    this.#startAutoPan();
  }

  #placeDragged() {
    const shift = this.#dragShift();

    this.drag.cards.forEach((node) => {
      const snapped = snapPosition({ x: node.originX + shift.x, y: node.originY + shift.y }, SNAP_GRID);
      const spot = this.#mcpMagnet(node.card, snapped);

      node.x = spot.x;
      node.y = spot.y;
      node.card.style.left = `${spot.x}px`;
      node.card.style.top = `${spot.y}px`;
    });

    this.#scheduleWires();
  }

  #settleMcpCards() {
    this.mcpCards.forEach((card) => {
      const spot = { x: parseFloat(card.style.left) || 0, y: parseFloat(card.style.top) || 0 };
      const settled = this.#mcpMagnet(card, spot);

      if (settled.y !== spot.y) card.style.top = `${settled.y}px`;
    });
  }

  #mcpMagnet(card, spot) {
    if (!card.dataset.nodeId.startsWith('mcp|')) return spot;
    if (this.drag?.cards.some((dragged) => dragged.id === card.dataset.stepId)) return spot;

    const host = this.#card(card.dataset.stepId);

    if (!host) return spot;

    const badge = card.querySelector('[data-role="badge"]') ?? card;
    const hostTop = parseFloat(host.style.top) || 0;
    const aligned = hostTop + host.offsetHeight / 2 - badge.offsetTop - badge.offsetHeight / 2;

    if (Math.abs(spot.y - aligned) > WIRE_SNAP_Y) return spot;

    return { x: spot.x, y: aligned };
  }

  #dragShift() {
    const { x, y, zoom } = this.viewport;

    return {
      x: (this.drag.pointer.x - this.drag.pointerX - (x - this.drag.viewportX)) / zoom,
      y: (this.drag.pointer.y - this.drag.pointerY - (y - this.drag.viewportY)) / zoom,
    };
  }

  #startAutoPan() {
    if (this.drag.frame) return;

    this.drag.frame = requestAnimationFrame(() => this.#autoPan());
  }

  #autoPan() {
    if (!this.drag) return;

    this.drag.frame = null;

    const rect = this.canvasTarget.getBoundingClientRect();
    const inside = { x: this.drag.pointer.x - rect.left, y: this.drag.pointer.y - rect.top };
    const [panX, panY] = calcAutoPan(inside, rect);

    if (panX === 0 && panY === 0) return;

    this.panZoom.setViewport({ x: this.viewport.x + panX, y: this.viewport.y + panY, zoom: this.viewport.zoom });
    this.#placeDragged();
    this.#startAutoPan();
  }

  #dragEnd() {
    this.#stopDragging();

    if (this.drag.moved) {
      this.#dropCards();
      this.#suppressClick(this.drag.card);
      this.#drawWires();
      this.dispatch('moved');
    }

    this.drag = null;
  }

  #dropCards() {
    this.drag.cards.forEach(({ id, x, y }) => {
      const node = this.#node(id);

      node.canvasX = x;
      node.canvasY = y;
    });
  }

  #stopDragging() {
    if (!this.drag) return;

    cancelAnimationFrame(this.drag.frame);
    window.removeEventListener('pointermove', this.dragMove);
    window.removeEventListener('pointerup', this.dragEnd);
  }

  #beginConnection(event, connection) {
    this.hovered = null;
    this.connection = { ...connection, pointerX: event.clientX, pointerY: event.clientY, moved: false };
    this.connectMove = (moving) => this.#connectMove(moving);
    this.connectEnd = (ending) => this.#connectEnd(ending);

    window.addEventListener('pointermove', this.connectMove);
    window.addEventListener('pointerup', this.connectEnd);
  }

  #connectMove(event) {
    const dx = event.clientX - this.connection.pointerX;
    const dy = event.clientY - this.connection.pointerY;

    if (!this.connection.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

    if (!this.connection.moved) {
      this.connection.moved = true;

      this.#drawWires();
    }

    const d = pathBetween(...this.#connectionEnds(event));

    if (this.tempWire) {
      this.tempWire.setAttribute('d', d);
    } else {
      this.tempWire = this.#path(d, 'var(--accent)', '2');
      this.tempWire.setAttribute('stroke-dasharray', '5 4');
      this.tempWire.setAttribute('pointer-events', 'none');
      this.wireLayer.appendChild(this.tempWire);
    }

    const under = this.#cardUnder(event);
    const welcoming = this.#reachable(this.connection.end, under) && under.dataset.nodeId !== this.connection.anchorId;

    this.#highlight(welcoming ? under : null);
  }

  #connectionEnds(event) {
    const rect = this.canvasTarget.getBoundingClientRect();
    const held = this.connection.end === 'target';
    const point = anchor(this.#anchorCard(), held ? 'bottom' : 'top');
    const pointer = {
      x: (event.clientX - rect.left - this.viewport.x) / this.viewport.zoom,
      y: (event.clientY - rect.top - this.viewport.y) / this.viewport.zoom,
    };

    return held ? [point, pointer] : [pointer, point];
  }

  #connectEnd(event) {
    window.removeEventListener('pointermove', this.connectMove);
    window.removeEventListener('pointerup', this.connectEnd);

    const connection = this.connection;

    this.tempWire?.remove();
    this.tempWire = null;
    this.connection = null;

    this.#highlight(null);

    if (!connection.moved) return;

    const target = this.#cardUnder(event);

    if (connection.parentId) {
      this.#endReconnect(connection, target);
      return;
    }

    this.#endConnect(connection, target);
    this.#suppressClick(this.#card(connection.anchorId));
  }

  #endConnect(connection, target) {
    if (!this.#reachable(connection.end, target)) return;

    this.dispatch('link', { detail: this.#linkEnds(connection, target.dataset.nodeId) });
  }

  #linkEnds({ anchorId, end }, nodeId) {
    if (end === 'target') return { sourceId: anchorId, targetId: nodeId };

    return { sourceId: nodeId, targetId: anchorId };
  }

  #endReconnect({ parentId, childId, end }, target) {
    if (!this.#reachable(end, target)) {
      this.#drawWires();
      return;
    }

    this.dispatch('relink', { detail: { parentId, childId, end, nodeId: target.dataset.nodeId } });
  }

  #reachable(end, target) {
    if (!target) return false;

    return end === 'source' || !target.dataset.triggerId;
  }

  #stopConnecting() {
    if (!this.connection) return;

    window.removeEventListener('pointermove', this.connectMove);
    window.removeEventListener('pointerup', this.connectEnd);
  }

  #anchorCard() {
    return this.#card(this.connection.anchorId);
  }

  #card(id) {
    return [...this.stepCards, ...this.triggerCards, ...(this.mcpCards ?? [])]
      .find((card) => card.dataset.nodeId === id);
  }

  #cardUnder(event) {
    return document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-node-id]') ?? null;
  }

  #highlight(card) {
    [...this.stepCards, ...this.triggerCards].forEach((node) => {
      node.classList.toggle('wire-target', node === card);
    });
  }

  #suppressClick(node) {
    node.addEventListener('click', (event) => event.stopPropagation(), { capture: true, once: true });
  }

  #node(id) {
    const { steps, triggers } = this.scene.workflow;

    if (id.startsWith('mcp|')) {
      const [, stepId, serverId] = id.split('|');

      return steps
        .find((step) => step.id === stepId)
        ?.mcpServers.find((attachment) => attachment.mcpServerId === serverId);
    }

    return steps.find((step) => step.id === id) ?? triggers.find((trigger) => trigger.id === id);
  }

  #mcpId(step, attachment) {
    return `mcp|${step.id}|${attachment.mcpServerId}`;
  }

  #mcpCardsOf(step) {
    return (step.mcpServers ?? [])
      .filter((attachment) => attachment.mcpServerId)
      .map((attachment, index) => this.#mcpCard(step, attachment, index));
  }

  #mcpCard(step, attachment, index) {
    const node = this.mcpCardTemplateTarget.content.firstElementChild.cloneNode(true);
    const server = this.scene.mcpCatalog.get(attachment.mcpServerId);
    const spot = this.#mcpSpot(step, attachment, index);
    const id = this.#mcpId(step, attachment);

    node.dataset.nodeId = id;
    node.dataset.stepId = step.id;
    node.style.left = `${spot.x}px`;
    node.style.top = `${spot.y}px`;
    this.#role(node, 'name').textContent = server?.name ?? '?';
    this.#mcpVisual(node, server);
    this.#paintCard(node, id, false);

    if (!this.scene.draggable) node.classList.remove('cursor-grab');

    return node;
  }

  #mcpSpot(step, attachment, index) {
    if (Number.isInteger(attachment.canvasX) && Number.isInteger(attachment.canvasY)) {
      return { x: attachment.canvasX, y: attachment.canvasY };
    }

    const base = this.#position(step);

    return { x: base.x + MCP_OFFSET_X, y: base.y + MCP_ALIGN_Y + index * MCP_GAP_Y };
  }

  #mcpVisual(node, server) {
    const monogram = this.#role(node, 'monogram');
    const icon = this.#role(node, 'icon');

    if (server?.iconUrl) {
      icon.src = server.iconUrl;
      icon.alt = server.name;
      icon.classList.remove('hidden');
      monogram.classList.add('hidden');

      return;
    }

    const name = server?.name ?? '?';
    const colors = MCP_PALETTE[this.#byteSum(name) % MCP_PALETTE.length];

    monogram.textContent = name.charAt(0).toUpperCase();
    monogram.classList.remove('hidden');
    monogram.classList.add(...colors.split(' '));
    icon.classList.add('hidden');
  }

  #byteSum(name) {
    return [...new TextEncoder().encode(name)].reduce((sum, byte) => sum + byte, 0);
  }

  #placeMcpCards() {
    this.scene.workflow.steps.forEach((step) => {
      (step.mcpServers ?? []).forEach((attachment, index) => {
        const card = this.#card(this.#mcpId(step, attachment));

        if (!card) return;

        const spot = this.#mcpSpot(step, attachment, index);

        card.style.left = `${spot.x}px`;
        card.style.top = `${spot.y}px`;
      });
    });
  }

  #role(node, role) {
    return node.querySelector(`[data-role="${role}"]`);
  }

  #tile(node, card) {
    const tile = this.#role(node, 'tile');

    tile.classList.remove(...Object.values(card.tones).flatMap((tone) => tone.split(' ')));
    tile.classList.add(...card.tones[card.iconValue].split(' '));

    for (const icon of tile.querySelectorAll(`[data-${card.iconAttribute}]`)) {
      this.#reveal(icon, icon.getAttribute(`data-${card.iconAttribute}`) === card.iconValue);
    }
  }

  #reveal(node, visible) {
    node.classList.toggle('hidden', !visible);
  }
}
